import { Environment } from "@p0security/shared/types/environment";
import { z } from "zod/v4";

/** Fetches and parses JSON config from the specified path */
const fetchConfig = async (path: string): Promise<unknown> => {
  const response = await fetch(path);
  if (!response.ok) {
    throw new Error(`Failed to fetch config: ${response.status}`);
  }
  const data = await response.json();
  return data;
};

const CONFIG_PATH = "/runtimeConfig.json";

const developer = process.env.P0_DEV_NAME;
const isLocal = process.env.P0_LOCAL;

/** Current environment determined from NODE_ENV and P0_ENVIRONMENT */
const environment: Environment =
  process.env.NODE_ENV === "production"
    ? process.env.P0_ENVIRONMENT === "stage"
      ? "stage"
      : process.env.P0_ENVIRONMENT === "prod-cna-central"
        ? "prod-cna-central"
        : process.env.P0_ENVIRONMENT === "prod-splunk-nonprod"
          ? "prod-splunk-nonprod"
          : process.env.P0_ENVIRONMENT === "prod-splunk-live"
            ? "prod-splunk-live"
            : process.env.P0_ENVIRONMENT === "prod-demo"
              ? "prod-demo"
              : "prod"
    : process.env.NODE_ENV === "development"
      ? process.env.P0_ENVIRONMENT === "test"
        ? "test"
        : "dev"
      : "test"; // unit tests

/** Generates default environment configuration from NODE_ENV and P0_ENVIRONMENT */
const getDefaultEnvironment = () => {
  if (environment === "dev") {
    if (!developer) {
      throw "Run app with `P0_DEV_NAME=hyphen-name yarn start`";
    }
    return {
      environment,
      environmentType: "devLike" as const,
      developer,
      apiUrl: isLocal
        ? "http://localhost:8088"
        : `https://${developer}.api.${environment}.p0.app`,
      frontendUrl: isLocal
        ? "http://localhost:8089"
        : `https://${developer}.frontend.${environment}.p0.app`,
      braekhusUrl: `https://${developer}.braekhus.${environment}.p0.app`,
      websocketUrl: isLocal
        ? `ws://localhost:8089/realtime`
        : `wss://${developer}.api.${environment}.p0.app/realtime`,
    };
  }

  switch (environment) {
    case "prod":
      return {
        environment,
        environmentType: "prodLike" as const,
        apiUrl: "https://api.p0.app",
        frontendUrl: "https://frontend.p0.app",
        braekhusUrl: "https://api.p0.app",
        websocketUrl: `wss://api.p0.app/realtime`,
      };
    case "prod-cna-central":
      return {
        environment,
        environmentType: "prodLike" as const,
        apiUrl: "https://api.cna.p0.app",
        frontendUrl: "https://frontend.cna.p0.app",
        braekhusUrl: "https://api.cna.p0.app",
        websocketUrl: `wss://api.cna.p0.app/realtime`,
      };
    case "prod-splunk-nonprod":
      return {
        environment,
        environmentType: "prodLike" as const,
        apiUrl: "https://api.splunk-nonprod.p0.app",
        frontendUrl: "https://frontend.splunk-nonprod.p0.app",
        braekhusUrl: "https://api.splunk-nonprod.p0.app",
        websocketUrl: `wss://api.splunk-nonprod.p0.app/realtime`,
      };
    case "prod-splunk-live":
      return {
        environment,
        environmentType: "prodLike" as const,
        apiUrl: "https://api.splunk.p0.app",
        frontendUrl: "https://frontend.splunk.p0.app",
        braekhusUrl: "https://api.splunk.p0.app",
        websocketUrl: `wss://api.splunk.p0.app/realtime`,
      };
    case "prod-demo":
      return {
        environment,
        environmentType: "prodLike" as const,
        apiUrl: "https://api.demo.p0.app",
        frontendUrl: "https://frontend.demo.p0.app",
        braekhusUrl: "https://api.demo.p0.app",
        websocketUrl: `wss://api.demo.p0.app/realtime`,
      };
    default:
      return {
        environment,
        environmentType: "prodLike" as const,
        apiUrl: `https://api.${environment}.p0.app`,
        frontendUrl: `https://frontend.${environment}.p0.app`,
        braekhusUrl: `https://api.${environment}.p0.app`,
        websocketUrl: `wss://api.${environment}.p0.app/realtime`,
      };
  }
};

const defaultEnvironment = getDefaultEnvironment();

const AuthConfigSchema = z.object({
  audience: z.string().optional(),
  clientId: z.string(),
  oidcUrl: z.url(),
  useSubAsEmail: z.boolean().optional().default(false),
});

const EnvironmentBaseSchema = z.object({
  environment: z.string().default(defaultEnvironment.environment),
  apiUrl: z.url().default(defaultEnvironment.apiUrl),
  frontendUrl: z.url().default(defaultEnvironment.frontendUrl),
  braekhusUrl: z.url().default(defaultEnvironment.braekhusUrl),
  websocketUrl: z.url().default(defaultEnvironment.websocketUrl),
});

const EnvironmentConfigSchema = z.discriminatedUnion("environmentType", [
  EnvironmentBaseSchema.extend({
    environmentType: z.literal("devLike"),
    developer: z.string(),
  }),
  EnvironmentBaseSchema.extend({
    environmentType: z.literal("prodLike"),
  }),
]);

const ConfigSchema = z
  .object({
    auth: AuthConfigSchema.optional(),
    environment: EnvironmentConfigSchema.prefault(defaultEnvironment),
  })
  .prefault({});

export type Config = z.infer<typeof ConfigSchema>;

const CONFIG: Config = await (async () => {
  const raw = await fetchConfig(CONFIG_PATH)
    .then((r) => {
      // eslint-disable-next-line no-console
      console.info(`[Config] Loaded config from %s`, CONFIG_PATH);
      return r;
    })
    .catch((err) => {
      console.warn(
        `[Config] Failed to load %s, using defaults:`,
        CONFIG_PATH,
        err instanceof Error ? err.message : String(err)
      );
      return undefined;
    });

  return ConfigSchema.parse(raw);
})();

export const getConfig = (): Config => CONFIG;
